/**
 * Styling for the DFC Fitness Village authentication panel. Every colour arrives through a
 * `DfcSurface` token or `DFC_PALETTE`; the one exception is the error red, which the brand
 * palette does not define — it comes from `DFC_NEUTRAL.error`, the same functional colour the
 * school-experience surfaces use, and is documented as a deliberate delta in the PR body.
 *
 * No `sx`, no inline styles, no colour literals — theme1's variant leans on `sx`; that is not
 * carried over here (plan §10).
 */
import styled from '@emotion/styled';
import { Box, ButtonBase, CircularProgress, Drawer, Typography } from '@mui/material';
import { DFC_PALETTE, DFC_NEUTRAL, withAlpha } from '../../../../../Utils/dfcBrand';
import { focusRing, omit, SurfaceProp } from '../../../../shared/dfcVillage/style';

/* ---------- shells ---------- */

/**
 * Bottom sheet (mobile). The paper carries the surface, rounded only at the top. It floats
 * over the page, so it takes the opaque `panel` fill — dark `surface` is a 5% white tint
 * that would let the page show through.
 */
export const SheetDrawer = styled(Drawer, omit('surface'))<SurfaceProp>`
  & .MuiPaper-root {
    background: ${({ surface }) => surface.panel};
    color: ${({ surface }) => surface.ink};
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    border-top: 1px solid ${({ surface }) => surface.border};
    max-height: 92vh;
    background-image: none;
  }

  & .MuiBackdrop-root {
    background: ${({ surface }) => withAlpha(surface.scrim, 0.6)};
  }
`;

/** Centred card (desktop). */
export const CardShell = styled(Box, omit('surface'))<SurfaceProp>`
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  background: ${({ surface }) => surface.surface};
  border: 1px solid ${({ surface }) => surface.border};
  border-radius: 20px;
  box-shadow: 0 18px 48px ${({ surface }) => withAlpha(surface.scrim, surface.dark ? 0.5 : 0.12)};
  overflow: hidden;
`;

/** Bare shell — no card chrome — for the inline court auth gate. */
export const InlineShell = styled(Box)`
  width: 100%;
`;

/** Round close control, top-right of the sheet. */
export const CloseButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    background: ${({ surface }) => surface.track};
    color: ${({ surface }) => surface.ink};
    ${({ surface }) => focusRing(surface)}

    svg {
      width: 18px;
      height: 18px;
    }

    .MuiTouchRipple-root {
      display: none;
    }
  }
`;

/* ---------- body ---------- */

/** Every control in the panel is a ButtonBase; the grey press ripple is off for all of them. */
export const PanelBody = styled(Box, omit('inline'))<{ inline?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: ${({ inline }) => (inline ? '0' : '32px 24px 28px')};

  .MuiTouchRipple-root {
    display: none;
  }

  @media (min-width: 600px) {
    padding: ${({ inline }) => (inline ? '0' : '32px')};
  }
`;

export const PanelHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const PanelTitle = styled(Typography, omit('surface'))<SurfaceProp>`
  && {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.15;
    color: ${({ surface }) => surface.ink};
  }
`;

export const PanelSubtitle = styled(Typography, omit('surface'))<SurfaceProp>`
  && {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${({ surface }) => surface.ink2};
  }
`;

/* ---------- tabs ---------- */

export const TabRow = styled(Box, omit('surface'))<SurfaceProp>`
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: ${({ surface }) => surface.track};
`;

export const TabButton = styled(ButtonBase, omit('surface', 'active'))<
  SurfaceProp & { active: boolean }
>`
  && {
    flex: 1;
    min-height: 40px;
    border-radius: 999px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    background: ${({ surface, active }) => (active ? surface.onBg : 'transparent')};
    color: ${({ surface, active }) => (active ? surface.onFg : surface.ink2)};
    transition: background 0.2s ease;
    ${({ surface }) => focusRing(surface)}
  }
`;

/* ---------- form ---------- */

export const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const FieldWrap = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled('label', omit('surface'))<SurfaceProp>`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ surface }) => surface.ink2};
`;

export const Field = styled('input', omit('surface', 'hasError'))<
  SurfaceProp & { hasError?: boolean }
>`
  width: 100%;
  box-sizing: border-box;
  height: 56px;
  padding: 0 52px 0 16px;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  color: ${({ surface }) => surface.ink};
  background: ${({ surface }) => surface.soft};
  border: 1px solid ${({ surface, hasError }) => (hasError ? DFC_NEUTRAL.error : surface.border)};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    color: ${({ surface }) => surface.ink3};
    opacity: 1;
  }

  /* Email and password are typed left to right (dir="ltr") but line up with the Arabic label. */
  [dir='rtl'] &[dir='ltr'] {
    /* @noflip */
    text-align: right;
  }

  &:focus {
    outline: none;
    border-color: ${({ surface, hasError }) =>
      hasError ? DFC_NEUTRAL.error : surface.dark ? DFC_PALETTE.green : surface.ink};
    box-shadow: 0 0 0 3px
      ${({ surface, hasError }) =>
        hasError
          ? withAlpha(DFC_NEUTRAL.error, 0.18)
          : withAlpha(surface.dark ? DFC_PALETTE.green : surface.ink, 0.14)};
  }
`;

/**
 * Password visibility toggle, sat inside the field: a 44px target centred on the 56px field
 * (label 17px + 6px gap above it).
 */
export const FieldAdornment = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    position: absolute;
    top: 29px;
    right: 4px;
    width: 44px;
    height: 44px;
    border-radius: 999px;
    color: ${({ surface }) => surface.ink3};
    ${({ surface }) => focusRing(surface)}

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const ForgotButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    align-self: flex-end;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    color: ${({ surface }) => (surface.dark ? DFC_PALETTE.green : surface.ink)};
    text-decoration: underline;
    text-underline-offset: 3px;
    ${({ surface }) => focusRing(surface)}

    &.Mui-disabled {
      opacity: 0.5;
      text-decoration: none;
    }
  }
`;

export const SubmitButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    margin-top: 4px;
    min-height: 56px;
    border-radius: 12px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 800;
    background: ${({ surface }) => surface.ctaBg};
    color: ${({ surface }) => surface.ctaFg};
    ${({ surface }) => focusRing(surface)}

    &.Mui-disabled {
      opacity: 0.5;
    }
  }
`;

export const Spinner = styled(CircularProgress, omit('surface'))<SurfaceProp>`
  && {
    color: ${({ surface }) => surface.ctaFg};
    width: 22px !important;
    height: 22px !important;
  }
`;

/* ---------- error ---------- */

/**
 * Rejected-sign-in / validation banner. The village surface scale defines no error colour,
 * so the functional red comes from `DFC_NEUTRAL.error` (as on the school surfaces).
 */
export const AuthError = styled(Box, omit('surface'))<SurfaceProp>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${withAlpha(DFC_NEUTRAL.error, 0.1)};
  border: 1px solid ${withAlpha(DFC_NEUTRAL.error, 0.4)};
  color: ${DFC_NEUTRAL.error};
  font-size: 12.5px;
  line-height: 1.45;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const AuthErrorList = styled('ul')`
  margin: 0;
  padding: 0;
  list-style: none;

  li + li {
    margin-top: 2px;
  }
`;

/* ---------- social ---------- */

export const SocialDivider = styled(Box, omit('surface'))<SurfaceProp>`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ surface }) => surface.ink3};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ surface }) => surface.border};
  }
`;

export const SocialRow = styled(Box)`
  display: flex;
  gap: 10px;
`;

export const SocialButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    flex: 1;
    min-height: 48px;
    gap: 8px;
    border-radius: 12px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    color: ${({ surface }) => surface.ink};
    background: ${({ surface }) => surface.surface};
    border: 1px solid ${({ surface }) => surface.border};
    ${({ surface }) => focusRing(surface)}

    &.Mui-disabled {
      opacity: 0.5;
    }
  }
`;

export const SocialIcon = styled('img')`
  width: 20px;
  height: 20px;
`;

/* ---------- inline gate heading (mirrors SchoolBooking/auth-gate) ---------- */

export const GateRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;
